// Global Types
import type {
  $Translate,
} from 'common.language';
import type {
  $Model,
  $ModelUpsertParams,
} from 'common.data';

// Global Enums
import {
  adminPages,
  adminSections,
} from 'common.enums';

// Global Components
import * as React from 'react';
import {
  Button,
  Card,
  Table,
} from 'reactstrap';
import {
  DeleteDialog,
  FormDialog,
  Loader,
  SectionHeader,
  TdDate,
} from '@vroskus/library-components';

// Helpers
import {
  cleanFormValues,
  isDiff,
} from '@vroskus/library-helpers/dist/browser';
import {
  getRegionUrl,
} from 'common.helpers/dist/browser';
import {
  defaultDeleteItem,
  defaultGetData,
  defaultUpsertItem,
  publicBorder,
} from '../../../helpers';
import config from '../../../config';

// Components
import Page from '../../layout/Page';
import EmptyTable from '../../common/EmptyTable';
import TdUrl from '../../common/TdUrl';
import CustomPageFields from '../../fields/CustomPageFields';
import ThQty from '../../common/ThQty';

// Services
import Services from '../../../services';

// Types
import type {
  $ReducerState,
} from '../../../redux';

export type $OwnProps = $Translate;

export type $StateProps = {
  main: $ReducerState<'main'>;
};

export type $DispatchProps = Record<string, unknown>;

type $Props = $DispatchProps & $OwnProps & $StateProps;

type $State = {
  data: Array<$Model<'CustomPage'>>;
  loading: boolean;
};

const pageId = adminPages.adminCustomPagesPage;

class AdminCustomPagesPageView extends React.Component<$Props, $State> {
  constructor(props: $Props) {
    super(props);

    this.state = {
      data: [],
      loading: false,
    };
  }

  componentDidMount(): void {
    this.getData();
    this.setupSocket();
  }

  componentDidUpdate(prevProps: $Props): void {
    const {
      main,
    } = this.props;

    if (main.activeRegionId !== null && isDiff(
      main.activeRegionId,
      prevProps.main.activeRegionId,
    )) {
      this.getData();
    }
  }

  setupSocket(): void {
    Services.Socket.on({
      action: (data) => {
        const {
          main,
        } = this.props;

        if (main.activeRegionId === data.RegionId && data.modelName === 'CustomPage') {
          this.getData();
        }
      },
      type: Services.Socket.type.modelChangedEvent,
    });
  }

  setStateAsync(state: Partial<$State>): Promise<void> {
    return new Promise((resolve) => {
      this.setState(
        (pervState: $State) => ({
          ...pervState,
          ...state,
        }),
        resolve,
      );
    });
  }

  async getData(): Promise<void> {
    const {
      main,
    } = this.props;

    const RegionId = main.activeRegionId;

    await defaultGetData({
      action: async () => {
        if (RegionId !== null) {
          await this.setStateAsync({
            loading: true,
          });

          const data = await Services.Api.getManyAdminCustomPage({
            RegionId,
          });

          await this.setStateAsync({
            data,
            loading: false,
          });
        }
      },
      errorAction: async () => this.setStateAsync({
        loading: false,
      }),
    });
  }

  getUrl(dataItem: $Model<'CustomPage'>): string {
    const {
      main,
    } = this.props;

    const baseUrl = getRegionUrl({
      mainDomain: config.DOMAIN,
      RegionId: dataItem.RegionId,
      regions: main.regions,
    });

    return `${baseUrl}/${dataItem.Slug || dataItem.id}`;
  }

  renderHeader() {
    const {
      main,
      t,
    } = this.props;

    const RegionId = main.activeRegionId;

    if (RegionId !== null) {
      const newItem: $ModelUpsertParams<'CustomPage'> = {
        Content: '',
        Name: '',
        Public: true,
        RegionId,
        Slug: null,
      };

      return (
        <Card id={adminSections[pageId].header}>
          <SectionHeader
            rightButtons={(
              <FormDialog
                fields={(
                  <CustomPageFields item={newItem} />
                )}
                id={'create-custom-page-dialog'}
                labelCloseButton={t('Close')}
                labelSaveButton={t('Save')}
                onAction={(values) => defaultUpsertItem({
                  action: async () => {
                    const params = cleanFormValues<$Model<'CustomPage'>>({
                      ...newItem,
                      ...values,
                    });

                    await Services.Api.upsertAdminCustomPage(params);
                  },
                })}
                subTitle={t('New')}
                title={t('Custom page')}
              >
                <Button
                  color={'info'}
                  id={'create-custom-page-button'}
                  size={'sm'}
                  title={t('Add new custom page')}
                >
                  {t('Add new')}
                </Button>
              </FormDialog>
            )}
            title={t('Custom pages')}
          />
        </Card>
      );
    }

    return null;
  }

  renderButtons(dataItem: $Model<'CustomPage'>) {
    const {
      t,
    } = this.props;

    const {
      id,
    } = dataItem;

    return (
      <div className={'d-flex justify-content-end'}>
        <FormDialog
          fields={(
            <CustomPageFields item={dataItem} />
          )}
          id={`update-custom-page-dialog-${id}`}
          labelCloseButton={t('Close')}
          labelSaveButton={t('Save')}
          onAction={(values) => defaultUpsertItem({
            action: async () => {
              const params = cleanFormValues<$Model<'CustomPage'>>({
                ...dataItem,
                ...values,
              });

              await Services.Api.upsertAdminCustomPage(params);
            },
          })}
          subTitle={dataItem.Name}
          title={t('Custom page')}
        >
          <Button
            className={'mr-1'}
            color={'warning'}
            id={`update-custom-page-button-${id}`}
            outline
            size={'sm'}
          >
            {t('Edit')}
          </Button>
        </FormDialog>
        <DeleteDialog
          content={t('Do you really want to delete?')}
          id={`delete-custom-page-dialog-${id}`}
          labelCloseButton={t('Close')}
          labelDeleteButton={t('Delete')}
          onAction={() => defaultDeleteItem({
            action: async () => Services.Api.deleteAdminCustomPage(dataItem),
          })}
          subTitle={dataItem.Name}
          title={t('Custom page')}
        >
          <Button
            color={'danger'}
            id={`delete-custom-page-button-${id}`}
            outline
            size={'sm'}
          >
            {t('Delete')}
          </Button>
        </DeleteDialog>
      </div>
    );
  }

  renderTable() {
    const {
      t,
    } = this.props;
    const {
      data,
    } = this.state;

    return data.length === 0 ? (
      <EmptyTable className={'mt-5'} />
    ) : (
      <Table responsive>
        <thead>
          <tr>
            <th>{t('Name')}</th>
            <th>{t('Url')}</th>
            <th>{t('Date created')}</th>
            <ThQty value={data.length} />
          </tr>
        </thead>
        <tbody>
          {data.map((dataItem: $Model<'CustomPage'>) => (
            <tr
              key={dataItem.id}
              style={publicBorder(dataItem.Public)}
            >
              <td>{dataItem.Name}</td>
              <TdUrl value={this.getUrl(dataItem)} />
              <TdDate
                duration
                multiple
                value={dataItem.createdAt}
              />
              <td>
                {this.renderButtons(dataItem)}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    );
  }

  renderContent() {
    const {
      loading,
    } = this.state;

    return (
      <Card id={adminSections[pageId].list}>
        <Loader
          loading={loading === true}
          overlay
        >
          {this.renderTable()}
        </Loader>
      </Card>
    );
  }

  render() {
    return (
      <Page id={pageId}>
        <>
          {this.renderHeader()}
          {this.renderContent()}
        </>
      </Page>
    );
  }
}

export default AdminCustomPagesPageView;
